export const LEGAL_DOCUMENT_TYPES = Object.freeze({
  TERMS: "terms",
  PRIVACY: "privacy",
});

export const LEGAL_DOCUMENT_ORDER = [
  LEGAL_DOCUMENT_TYPES.TERMS,
  LEGAL_DOCUMENT_TYPES.PRIVACY,
];

export const LEGAL_ACTIONS_PATH = "/legal/actions";

export const LEGAL_REQUIRED_ACTIONS = Object.freeze({
  NONE: "none",
  NOTICE: "notice",
  ACKNOWLEDGEMENT: "acknowledgement",
  RENEWED_CONSENT: "renewed_consent",
});

export const LEGAL_SUBMISSION_ACTIONS = Object.freeze({
  PRESENTED: "presented",
  ACKNOWLEDGED: "acknowledged",
  CONSENTED: "consented",
  DECLINED: "declined",
});

const LEGAL_DOCUMENT_PATHS = Object.freeze({
  [LEGAL_DOCUMENT_TYPES.TERMS]: "/legal/terms",
  [LEGAL_DOCUMENT_TYPES.PRIVACY]: "/legal/privacy",
});

const LEGAL_DOCUMENT_FALLBACKS = Object.freeze({
  [LEGAL_DOCUMENT_TYPES.TERMS]: {
    title: "Terms & Conditions",
    effectiveDateLabel: "Last Updated",
  },
  [LEGAL_DOCUMENT_TYPES.PRIVACY]: {
    title: "Privacy Policy",
    effectiveDateLabel: "Last review and update",
  },
});

export function normalizeLegalDocumentType(value) {
  if (value === LEGAL_DOCUMENT_TYPES.PRIVACY) {
    return LEGAL_DOCUMENT_TYPES.PRIVACY;
  }

  return LEGAL_DOCUMENT_TYPES.TERMS;
}

export function buildLegalDocumentPath(documentType, search = "") {
  const normalizedDocumentType = normalizeLegalDocumentType(documentType);
  const pathname = LEGAL_DOCUMENT_PATHS[normalizedDocumentType];

  if (!search) {
    return pathname;
  }

  return `${pathname}${search.startsWith("?") ? search : `?${search}`}`;
}

export function getLegalDocumentMetadata(t, documentType) {
  const normalizedDocumentType = normalizeLegalDocumentType(documentType);
  const prefix = `documents.${normalizedDocumentType}`;
  const fallbacks = LEGAL_DOCUMENT_FALLBACKS[normalizedDocumentType];

  return {
    documentType: normalizedDocumentType,
    title: t(`${prefix}.title`, {
      ns: "legal",
      defaultValue: fallbacks.title,
    }),
    version: t(`${prefix}.version`, {
      ns: "legal",
      defaultValue: "",
    }),
    effectiveDateLabel: t(`${prefix}.effectiveDateLabel`, {
      ns: "legal",
      defaultValue: fallbacks.effectiveDateLabel,
    }),
    effectiveDateText: t(`${prefix}.effectiveDateText`, {
      ns: "legal",
      defaultValue: "",
    }),
    sourceUrl: t(`${prefix}.sourceUrl`, {
      ns: "legal",
      defaultValue: "",
    }),
    body: t(`${prefix}.body`, {
      ns: "legal",
      returnObjects: true,
      defaultValue: [],
    }),
  };
}

export function getResolvedLegalDocuments(t, legalStatus = null) {
  return LEGAL_DOCUMENT_ORDER.map((documentType) => {
    const metadata = getLegalDocumentMetadata(t, documentType);
    const resolved = legalStatus?.documents?.[documentType] || null;

    return {
      ...metadata,
      ...resolved,
      documentType,
      title: resolved?.title || metadata.title,
      version: resolved?.version || metadata.version,
      effectiveDateLabel: resolved?.effectiveDateLabel || metadata.effectiveDateLabel,
      effectiveDateText: resolved?.effectiveDateText || metadata.effectiveDateText,
      sourceUrl: resolved?.sourceUrl || metadata.sourceUrl,
      available:
        typeof resolved?.available === "boolean"
          ? resolved.available
          : Boolean(metadata.sourceUrl),
    };
  });
}

export function getActionableLegalDocuments(legalStatus = null) {
  return getResolvedLegalDocuments(() => "", legalStatus).filter(
    (document) =>
      Array.isArray(document.allowedActions) && document.allowedActions.length > 0,
  );
}

export function hasBlockingLegalAction(legalStatus = null) {
  return Boolean(legalStatus?.appUseBlocked);
}

export function isProcessingAllowed(legalStatus = null, processing) {
  if (!processing) {
    return true;
  }

  const restrictions = Array.isArray(legalStatus?.processingRestrictions)
    ? legalStatus.processingRestrictions
    : [];

  return !restrictions.some(
    (restriction) =>
      restriction?.processing === processing && restriction?.allowed === false,
  );
}
